import { Box, Card, Button, TextField, InputAdornment, IconButton, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { usePageTitle } from "contexts/PageTitleContext";
import { Add, Search, Clear as ClearIcon, Refresh } from "@mui/icons-material";
import CropHubTable from "page-sections/CropHub/CropHubTable";
import CropHubAddUpdate from "page-sections/CropHub/CropHubAddUpdate";
// ─── CHANGE 1: Import buttonConfig ─────────────────────────────────────────────
import buttonConfig from "config/ConfigAccessButton";
// ─────────────────────────────────────────────────────────────────────────────

const CropHub = () => {
    const { setPageTitle } = usePageTitle();
    const [searchInput, setSearchInput] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const handleRefresh = () => {
        setRefreshKey((prev) => prev + 1);
    };

    useEffect(() => {
        setPageTitle('Crop Hub');
    }, [setPageTitle]);

    // ─── CHANGE 2: Setup permissions from config ─────────────────────────────
    const cropHubConfig = buttonConfig["Crop hub"] || {};
    const can = (action) => !!cropHubConfig[action];
    // ─────────────────────────────────────────────────────────────────────────

    const handleSearch = () => {
        const value = searchInput.trim();
        if (!value) {
            handleClearSearch();
            return;
        }
        setSearchValue(value);
        setHasSearched(true);
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearchValue("");
        setHasSearched(false);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    return (
        <Box pt={2} pb={4}>
            <Card sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            placeholder="Search by Crop Name, Scientific Name, Countries"
                            value={searchInput}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchInput(value);
                                if (hasSearched && value !== searchValue) {
                                    setHasSearched(false);
                                }
                                if (!value.trim() && (searchValue || hasSearched)) {
                                    handleClearSearch();
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput && (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            size="small" 
                                            onClick={handleClearSearch} 
                                            edge="end"
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { height: 40 }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                    borderRadius: 2
                                }
                            }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSearch}
                                startIcon={<Search />}
                                disabled={!searchInput.trim() || (searchInput.trim() === searchValue && hasSearched)}
                                sx={{ 
                                    borderRadius: 2, 
                                    textTransform: "none", 
                                    height: 40,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Search
                            </Button>
                            
                            {/* ─── CHANGE 3: Add Crop button — disabled when no add_crop permission ─── */}
                            <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={() => can('add_crop') && handleExportClick()}
                                disabled={!can('add_crop')}
                                sx={{ 
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    height: 40,
                                    boxShadow: 'none',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Add Crop
                            </Button>
                            {/* ─────────────────────────────────────────────────────────────────────── */}

                            <Button
                                variant="contained"
                                onClick={handleRefresh}
                                startIcon={<Refresh />}
                                sx={{ 
                                    minWidth: "auto",
                                    px: 2,
                                    borderRadius: 2,
                                    height: 40,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            <Card>
                <Box sx={{ mt: 1 }}>
                    <CropHubTable
                        key={refreshKey}
                        refreshKey={refreshKey}
                        searchQuery={searchValue}
                        loading={false}
                    />
                </Box>
            </Card>

            <CropHubAddUpdate  
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={handleRefresh} 
            />
        </Box>
    );
};

export default CropHub;